import React from 'react';
import './OwnerStatCards.css';

/**
 * OwnerStatCards — 3 KPI cards for the TurnKey Owner overview tab.
 *
 * Brand palette v2.1 + admin white-card pattern: white background, 5px left
 * accent bar, icon + value + grey sublabel. Revenue is emphasized through
 * hierarchy (largest value, boldest, leads the row) — not a coloured fill —
 * so contrast stays high and orange is reserved for warnings/highlights only.
 *
 * Props:
 *   dashboardStats: { totalRevenue, revenuePerNight, occupancyRate, totalBookings, averageStayDuration }
 *   periodInProgress: the selected range has not closed yet, so the figures cover
 *     elapsed nights only (backend-derived — see revenueRoutes parseDateRange).
 */

// Shared admin-pattern card shell (white bg, left accent bar set per-card)
const cardBase = {
  backgroundColor: '#fff',
  borderRadius: '8px',
  padding: 'clamp(16px, 5vw, 25px)',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  minHeight: '140px',
  display: 'flex',
  alignItems: 'center',
  gap: 'clamp(12px, 3vw, 16px)'
};

const labelStyle = { margin: '4px 0 0', fontSize: 'clamp(14px, 3vw, 16px)', fontWeight: 600, color: '#333' };
const subStyle = { color: '#666', fontSize: 'clamp(11px, 2.5vw, 13px)' };

const OwnerStatCards = ({ dashboardStats, periodInProgress = false }) => (
  <div className="performance-metrics" dir="rtl" style={{ marginBottom: '30px' }}>
    <div className="owner-stat-grid">

      {/* Revenue — primary metric: purple accent + purple value, largest & boldest */}
      <div style={{ ...cardBase, borderLeft: '5px solid #471396' }}>
        <i className="bi bi-cash-coin" style={{ fontSize: 'clamp(28px, 7vw, 36px)', color: '#471396' }}></i>
        <div>
          <h2 style={{ margin: 0, fontSize: 'clamp(28px, 7vw, 42px)', fontWeight: 800, color: '#471396', lineHeight: 1.1 }}>
            {(dashboardStats.totalRevenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} ر.س
          </h2>
          <p style={labelStyle}>إجمالي الإيرادات</p>
          <small style={subStyle}>
            متوسط الليلة: {(dashboardStats.revenuePerNight || 0).toFixed(0)} ر.س
          </small>
        </div>
      </div>

      {/* Occupancy — green accent (positive metric), navy value */}
      <div style={{ ...cardBase, borderLeft: '5px solid #48b58d' }}>
        <i className="bi bi-graph-up-arrow" style={{ fontSize: 'clamp(24px, 6vw, 32px)', color: '#48b58d' }}></i>
        <div>
          <h2 style={{ margin: 0, fontSize: 'clamp(24px, 6vw, 34px)', fontWeight: 700, color: '#090040', lineHeight: 1.1 }}>
            {Number((dashboardStats.occupancyRate || 0).toFixed(1))}%
          </h2>
          <p style={labelStyle}>معدل الإشغال</p>
          {/* A range ending today counts elapsed nights only — say so rather than let
              the figure read as a closed-period result. */}
          <small style={subStyle}>
            {periodInProgress ? 'حتى اليوم · الليالي المنقضية فقط' : 'خلال الفترة المحددة'}
          </small>
        </div>
      </div>

      {/* Bookings — navy accent + navy value */}
      <div style={{ ...cardBase, borderLeft: '5px solid #090040' }}>
        <i className="bi bi-calendar-check" style={{ fontSize: 'clamp(24px, 6vw, 32px)', color: '#090040' }}></i>
        <div>
          <h2 style={{ margin: 0, fontSize: 'clamp(24px, 6vw, 34px)', fontWeight: 700, color: '#090040', lineHeight: 1.1 }}>
            {dashboardStats.totalBookings || 0}
          </h2>
          <p style={labelStyle}>الحجوزات خلال الفترة</p>
          <small style={subStyle}>
            متوسط الإقامة: {dashboardStats.averageStayDuration || 0} ليلة
          </small>
        </div>
      </div>

    </div>
  </div>
);

export default OwnerStatCards;
